import cx from "classnames";

/** A titled panel. The portal's only container, so every surface reads the same. */
export const SponsorCard = ({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <section
    className={cx(
      "rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900 sm:p-6",
      className,
    )}
  >
    {(title || action) && (
      <div className="mb-4 flex items-start justify-between gap-3">
        {title && (
          <h2 className="text-base font-semibold text-nearly-black dark:text-neutral-100">
            {title}
          </h2>
        )}
        {action}
      </div>
    )}
    {children}
  </section>
);

/**
 * A headline figure. `hint` carries the qualifier that stops a number being misread — the annual
 * total is stated beneath the monthly one rather than beside it, so the two are never mistaken
 * for alternatives.
 */
export const SponsorStat = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) => (
  <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900 sm:p-5">
    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
      {label}
    </p>
    <p className="mt-2 text-2xl font-semibold text-nearly-black dark:text-neutral-100 sm:text-3xl">
      {value}
    </p>
    {hint && <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{hint}</p>}
  </div>
);

export const SponsorPill = ({ label, className }: { label: string; className?: string }) => (
  <span
    className={cx(
      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
      className,
    )}
  >
    {label}
  </span>
);

export const SponsorButton = ({
  children,
  onClick,
  variant = "primary",
  disabled,
  type = "button",
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "danger";
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={cx(
      "inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
      variant === "primary" && "bg-brand-color text-white hover:opacity-90",
      variant === "secondary" &&
        "border border-neutral-300 text-nearly-black hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800",
      variant === "danger" && "bg-red text-white hover:opacity-90",
      className,
    )}
  >
    {children}
  </button>
);

/** The portal's single empty state, so "nothing here" reads the same on every screen. */
export const SponsorEmptyState = ({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
}) => (
  <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
    <p className="text-base font-semibold text-nearly-black dark:text-neutral-100">{title}</p>
    <p className="max-w-md text-sm text-neutral-500 dark:text-neutral-400">{body}</p>
    {action && <div className="mt-3">{action}</div>}
  </div>
);
